
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from "@/components/ui/card";
import { ExternalLink } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import { supabase } from "@/integrations/supabase/client";
import ReactMarkdown from 'react-markdown';
import { 
  Carousel, 
  CarouselContent, 
  CarouselItem, 
  CarouselNext, 
  CarouselPrevious 
} from "@/components/ui/carousel";
import LocalNewsSection from './LocalNewsSection';

export interface NewsItem {
  id: string;
  title: string;
  thumbnailUrl: string;
  videoUrl: string;
}

export const NewsPreview = () => {
  const isMobile = useIsMobile();
  const [newsItems, setNewsItems] = useState<NewsItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchNews = async () => {
      try {
        const { data, error } = await supabase
          .from('news')
          .select('id, title, videoid, publishedat')
          .order('publishedat', { ascending: false })
          .limit(4);
        
        if (error) {
          console.error('Error fetching news:', error);
          return;
        }
        
        // Ensure we have valid data before transforming
        if (data && data.length > 0) {
          const transformedData: NewsItem[] = data.map(item => ({
            id: item.id ? item.id.toString() : `news-${Date.now()}`,
            title: item.title || 'Untitled Video',
            thumbnailUrl: item.videoid ? 
              `https://i.ytimg.com/vi/${item.videoid}/maxresdefault.jpg` : 
              '/placeholder.svg',
            videoUrl: item.videoid ? 
              `https://www.youtube.com/watch?v=${item.videoid}` : 
              '#'
          }));
          
          setNewsItems(transformedData);
        } else {
          // If no data, use placeholders so UI doesn't break
          setNewsItems([
            {
              id: 'placeholder-1',
              title: '영상 준비 중입니다',
              thumbnailUrl: '/placeholder.svg',
              videoUrl: '#'
            },
            {
              id: 'placeholder-2',
              title: '영상 준비 중입니다',
              thumbnailUrl: '/placeholder.svg',
              videoUrl: '#'
            },
            {
              id: 'placeholder-3',
              title: '영상 준비 중입니다',
              thumbnailUrl: '/placeholder.svg',
              videoUrl: '#'
            }
          ]);
        }
      } catch (error) {
        console.error('Failed to fetch news:', error);
        // Use placeholders on error
        setNewsItems([
          {
            id: 'placeholder-1',
            title: '영상 준비 중입니다',
            thumbnailUrl: '/placeholder.svg',
            videoUrl: '#'
          },
          {
            id: 'placeholder-2',
            title: '영상 준비 중입니다',
            thumbnailUrl: '/placeholder.svg',
            videoUrl: '#'
          },
          {
            id: 'placeholder-3',
            title: '영상 준비 중입니다',
            thumbnailUrl: '/placeholder.svg',
            videoUrl: '#'
          }
        ]);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchNews();
  }, []);

  const renderNewsItem = (item: NewsItem, isMobileView = false) => (
    <a 
      key={item.id}
      href={item.videoUrl} 
      target="_blank" 
      rel="noopener noreferrer"
      className="group hover:opacity-90 transition-opacity"
    >
      <div className={`relative ${isMobileView ? 'pb-[54.84375%]' : 'pb-[56.25%]'} rounded-md overflow-hidden mb-1.5 border border-border/50 bg-secondary`}>
        <img 
          src={item.thumbnailUrl} 
          alt={item.title}
          className="absolute inset-0 w-full h-full object-cover"
          onError={(e) => {
            const target = e.target as HTMLImageElement;
            target.src = "/placeholder.svg";
          }}
          loading="eager"
        />
        <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
          <div className={`${isMobileView ? 'w-6 h-6' : 'w-10 h-10'} rounded-full bg-primary/80 flex items-center justify-center`}>
            <svg xmlns="http://www.w3.org/2000/svg" width={isMobileView ? "14" : "20"} height={isMobileView ? "14" : "20"} viewBox="0 0 24 24" fill="white" className="ml-1">
              <polygon points="5 3 19 12 5 21 5 3"></polygon>
            </svg>
          </div>
        </div>
      </div>
      <div className={`${isMobileView ? 'text-xs' : 'text-sm'} font-medium line-clamp-2 group-hover:text-primary transition-colors prose prose-sm max-w-none dark:prose-invert`}>
        <ReactMarkdown>{item.title}</ReactMarkdown>
      </div>
    </a>
  );

  if (isLoading) {
    return (
      <Card className="mb-4">
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Local News Loading */}
            <div className="md:col-span-1">
              <div className="flex justify-between items-center mb-3">
                <h2 className="text-lg font-medium">최신로컬뉴스</h2>
              </div>
              <div className="space-y-2">
                {[1, 2, 3, 4].map(i => (
                  <div key={i} className="h-4 w-full bg-muted rounded" />
                ))}
              </div>
            </div>
            
            {/* YouTube News Loading */}
            <div className="md:col-span-2">
              <div className="flex justify-between items-center mb-3">
                <h2 className="text-lg font-medium">최신 뉴스</h2>
              </div>
              <div className="grid grid-cols-3 gap-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="space-y-2">
                    <div className="relative pb-[56.25%] rounded-md overflow-hidden bg-muted">
                      <div className="absolute inset-0 w-full h-full" />
                    </div>
                    <div className="h-4 w-full bg-muted rounded" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="mb-4">
      <CardContent className="p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Left side: Local News - 1/3 width */}
          <div className="md:col-span-1">
            <div className="mb-3">
              <Link to="/news" className="inline-block hover:text-primary transition-colors">
                <h2 className="text-lg font-medium">최신로컬뉴스</h2>
              </Link>
            </div>
            <LocalNewsSection />
          </div>
          
          {/* Right side: YouTube News - 2/3 width */}
          <div className="md:col-span-2">
            <div className="flex justify-between items-center mb-3">
              <Link to="/news" className="inline-block hover:text-primary transition-colors">
                <h2 className="text-lg font-medium">최신 뉴스</h2>
              </Link>
            </div>
            
            {isMobile ? (
              <Carousel className="w-full" opts={{ slidesToScroll: 1 }}>
                <CarouselContent>
                  {newsItems.slice(0, 3).map((item) => (
                    <CarouselItem key={item.id} className="basis-1/3">
                      {renderNewsItem(item, true)}
                    </CarouselItem>
                  ))}
                </CarouselContent>
                <div className="flex justify-center mt-2">
                  <CarouselPrevious className="static translate-y-0 mx-1 h-7 w-7" />
                  <CarouselNext className="static translate-y-0 mx-1 h-7 w-7" />
                </div>
              </Carousel>
            ) : (
              <div className="grid grid-cols-3 gap-3">
                {newsItems.slice(0, 3).map(item => renderNewsItem(item))}
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
